import eligibilityCheckServices from "./eligibilityCheckService";

interface Country {
  countryId: string;
  countryDesc: string;
  active: boolean;
  createdOn: string | null;
  createdBy: string | null;
  modifiedOn: string | null;
  modifiedBy: string | null;
  createdName: string | null;
  modifiedName: string | null;
}

interface University {
  universityId: number;
  universityCode: string;
  universityName: string;
  startDate: number;
  expiryDate: number;
  countryId: string;
  universityHostCode: string | null;
  universityRank: string | null;
  active: boolean;
  createdOn: string | null;
  createdBy: string | null;
  modifiedOn: string | null;
  modifiedBy: string | null;
  createdName: string | null;
  modifiedName: string | null;
}

interface Institute {
  instituteId: number;
  instituteCode: string;
  instituteName: string;
  instituteAddress: string | null;
  addressLine1: string;
  universityId: number;
  startDate: number;
  expiryDate: number;
  contactPhoneNo1: string;
  contactPhoneNo2: string;
  contactPersonName: string;
  contactPersonPhoneNo: string;
  contactPersonEmail: string;
  instituteHostCode: string | null;
  rank: string | null;
  active: boolean;
  createdOn: string | null;
  createdBy: string | null;
  modifiedOn: string | null;
  modifiedBy: string | null;
  createdName: string | null;
  modifiedName: string | null;
}

interface Course {
  courseId: number;
  courseCode: string;
  courseName: string;
  courseType: number;
  courseStream: number;
  courseSubStream: number;
  hostCode: string;
  courseDuration: number;
  startDate: number;
  expiryDate: number;
  version: number;
  lastmntBy: number;
  lastmntOn: number;
  recordStatus: string;
  roleCode: string;
  nextRoleCode: string;
  taskId: string;
  nextTaskId: string;
  recordType: string;
  workflowId: number;
  timeOfTheChange: number;
  timeOfCommit: number;
  seqNo: string;
}

interface DropdownResponse<T> {
  status: string;
  message: string;
  data: T[];
}
interface Salutations {
  salutationCode: string;
  salutationDesc: string;
}

interface MaritalStatus {
  id?: string | number;
  statusId?: string | number;
  code?: string;
  value?: string;
  label?: string;
  name?: string;
  desc?: string;
  maritalStatusCode?: string;
  maritalStatusDesc?: string;
}

interface Nationality {
  nationalityCode?: string;
  nationalityName?: string;
  nationalityDesc?: string;
  code?: string;
  value?: string;
  label?: string;
  name?: string;
}

const DUMMY_NATIONALITIES: { value: string; label: string }[] = [
  
];
interface UniversitiesRequest {
  universityName?: string;
  countryId: string;
}

interface InstitutesRequest {
  instituteName?: string;
  universityId: string;
  countryId: string;
}

interface CoursesRequest {
  courseName?: string;
  instituteId: any;
}

// Marital status and nationality masters are requested from several
// independent places at once (useApplicantMasterData is mounted by multiple
// sibling components, plus a few direct callers) — cache the in-flight/
// resolved promise so the underlying API is only actually hit once per load,
// no matter how many callers ask for it.
let maritalStatusMasterPromise: Promise<DropdownResponse<MaritalStatus>> | null = null;
let nationalityListPromise: Promise<any[]> | null = null;
let countriesPromise: Promise<DropdownResponse<Country>> | null = null;

const dropdownService = {
  getCountries: (): Promise<DropdownResponse<Country>> => {
    if (!countriesPromise) {
      countriesPromise = eligibilityCheckServices
        .getCountries()
        .then((response) => response.data)
        .catch((error) => {
          countriesPromise = null;
          throw error;
        });
    }
    return countriesPromise;
  },

  getUniversities: async (
    requestData: UniversitiesRequest
  ): Promise<DropdownResponse<University>> => {
    const response = await eligibilityCheckServices.getUniversities(
      requestData
    );
    return response.data;
  },

  getInstitutes: async (
    requestData: InstitutesRequest
  ): Promise<DropdownResponse<Institute>> => {
    const response = await eligibilityCheckServices.getInstitutes(requestData);
    return response.data;
  },

  getCourses: async (
    requestData: CoursesRequest
  ): Promise<DropdownResponse<Course>> => {
    const response = await eligibilityCheckServices.getCourses(requestData);
    return response.data;
  },

  getMasters: async (): Promise<any> => {
    const response = await eligibilityCheckServices.getMasters();
    return response.data;
  },

  getProducts: async (): Promise<{
    status: string;
    message: string;
    data: { code: string; name: string }[];
  }> => {
    const response = await eligibilityCheckServices.getProducts();
    return response.data;
  },
  getSalutations: async (): Promise<DropdownResponse<Salutations>> => {
    const response = await eligibilityCheckServices.getSalutations();
    return response.data;
  },

  getMaritalStatusMaster: (): Promise<DropdownResponse<MaritalStatus>> => {
    if (!maritalStatusMasterPromise) {
      maritalStatusMasterPromise = eligibilityCheckServices
        .getMaritalStatusMaster()
        .then((response) => response.data)
        .catch((error) => {
          maritalStatusMasterPromise = null;
          throw error;
        });
    }
    return maritalStatusMasterPromise;
  },

  getAddressTypes: async (): Promise<{ value: string; label: string }[]> => {
    try {
      const response = await eligibilityCheckServices.getAddressTypes();
      const data = response?.data?.data || response?.data || [];
      const mapped = (Array.isArray(data) ? data : []).map((item: any) => ({
        value: String(item?.addressTypeCode?? ""),
        label: String(item?.addressTypeDiscription ?? ""),
      })).filter((opt: any) => opt.value && opt.label && opt.value !== "undefined");
      if (mapped.length > 0) return mapped;
    } catch {
      // fall through to dummy data
    }
    return [
      { value: "RESIDENTIAL", label: "Residential" },
    ];
  },

  getPropertyTypes: async (): Promise<{ value: string; label: string; serialId?: number }[]> => {
    try {
      const response = await eligibilityCheckServices.getPropertyTypes();
      const data = response?.data?.data || response?.data || [];
      const mapped = (Array.isArray(data) ? data : []).map((item: any) => ({
        value: String(item?.code ?? ""),
        label: String(item?.value ?? ""),
        serialId: item?.serialId != null ? Number(item.serialId) : undefined,
      })).filter((opt: any) => opt.value && opt.label && opt.value !== "undefined");
      if (mapped.length > 0) return mapped;
    } catch {
      // fall through to dummy data
    }
    return [
      { value: "LAND", label: "Land" },
    ];
  },

  getPropertySubTypes: async (propertyTypeCode?: string): Promise<{ value: string; label: string; serialId?: number }[]> => {
    try {
      const response = await eligibilityCheckServices.getPropertySubTypes(propertyTypeCode);
      const data = response?.data?.data || response?.data || [];
      const mapped = (Array.isArray(data) ? data : []).map((item: any) => ({
        value: String(item?.code ??  ""),
        label: String(item?.value ?? ""),
        serialId: item?.serialId != null ? Number(item.serialId) : undefined,
      })).filter((opt: any) => opt.value && opt.label && opt.value !== "undefined");
      if (mapped.length > 0) return mapped;
    } catch {
      // fall through to dummy data
    }
    // Dummy data - filtered by propertyTypeCode if provided
    const allSubTypes: Record<string, { value: string; label: string }[]> = {
      
    };
    if (propertyTypeCode && allSubTypes[propertyTypeCode]) {
      return allSubTypes[propertyTypeCode];
    }
    return Object.values(allSubTypes).flat();
  },

  getNationalityList: (): Promise<any[]> => {
    if (!nationalityListPromise) {
      nationalityListPromise = eligibilityCheckServices
        .getNationalityMaster()
        .then((response) => {
          const data: Nationality[] = response?.data?.data || response?.data || [];
          const mapped = data
            .map((item) => ({
              value: String(item?.nationalityCode ?? ""),
              label: String(item?.nationalityDesc ?? ""),
            }))
            .filter(
              (opt) =>
                opt.value &&
                opt.label &&
                opt.value !== "undefined" &&
                opt.label !== "undefined"
            );
          return mapped.length > 0 ? mapped : DUMMY_NATIONALITIES;
        })
        .catch(() => {
          // fall through to dummy data, and allow a future call to retry
          nationalityListPromise = null;
          return DUMMY_NATIONALITIES;
        });
    }
    return nationalityListPromise;
  },
};

export default dropdownService;
